import { type QueryResult, type OperationVariables } from '@apollo/react-common';
import type { ApolloQueryResult } from 'apollo-client';
import {
	type QueryHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useQuery,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { useCallback, useState, useEffect } from 'react';
import { type ConcreteRequest } from 'relay-runtime';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { fetchQuery } from './fetchQuery.ts';
import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

export type RefetchSupportedType<TData, TVariables> = (
	vars?: TVariables,
) => Promise<Pick<ApolloQueryResult<TData>, 'data' | 'errors'>>;

export type UseQueryInteropSupportedResult<TData, TVariables> = Pick<
	QueryResult<TData, TVariables>,
	| 'data'
	| 'loading'
	| 'variables'
	| 'startPolling'
	| 'stopPolling'
	| 'fetchMore'
	| 'networkStatus'
	| 'client' // TODO: deprecate client usage in hook return values in favor of direct access to interop shims and/or respective hooks
> & {
	refetch: RefetchSupportedType<TData, TVariables>;
	error?: ApolloErrorInterop;
};

export type UseQueryInteropSupportedHookOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryHookOptions<TData, TVariables>,
	| 'variables'
	| 'skip'
	| 'onError'
	| 'client'
	| 'fetchPolicy'
	| 'errorPolicy'
	| 'pollInterval'
	| 'notifyOnNetworkStatusChange'
	| 'onError'
	| 'onCompleted'
	| 'context'
>;

export type UseQueryInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [GraphQLTaggedNode, UseQueryInteropSupportedHookOptions<TData, TVariables>];

export function useQueryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	...[query, params]: UseQueryInteropSupportedArgs<TData, TVariables>
): UseQueryInteropSupportedResult<TData, TVariables> {
	if (process.env.BUILD_VARIANT === 'relay') {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useQueryInteropRelay<TData, TVariables>(...[query, params]);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useQuery<TData, TVariables>(
			...[
				toDocumentNode(query),
				{
					...params,
					context: { ...params?.context, requestId: (query as ConcreteRequest).params?.id },
				},
			],
		);
	}
}

function useQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	query: GraphQLTaggedNode,
	options?: Parameters<typeof useQuery<TData, TVariables>>[1],
): UseQueryInteropSupportedResult<TData, TVariables> {
	const environment = useRelayEnvironment();

	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables);

	if (!('params' in query)) {
		throw new Error(
			'[Relay Migration] unsupported tagged node provided. Please make sure you generated Relay artifacts for your operation',
		);
	}

	const skip = options?.skip || false;
	const context = options?.context;

	// State to manage the query result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(!skip);
	const [error, setError] = useState<any>(undefined);
	const onError = useStableCallback(options?.onError || function noop() {});

	// Use fetchQuery to get data
	useEffect(() => {
		if (skip) {
			setLoading(false);
			setData(undefined);
			setError(undefined);
			return;
		}

		setLoading(true);
		setError(undefined);

		const subscription = fetchQuery<{ response: TData; variables: TVariables }>(
			environment,
			query,
			variables,
			{
				networkCacheConfig: { metadata: context },
			},
		).subscribe({
			next: (result) => {
				setData(result);
				setLoading(false);
			},
			error: (err: any) => {
				setError(err);
				setLoading(false);
				onError(err);
			},
		});

		return () => subscription.unsubscribe();
	}, [environment, query, variables, skip, onError, context]);

	// Create a refetch function for Apollo compatibility
	const refetch: RefetchSupportedType<TData, TVariables> = useCallback(
		(newVariables?: TVariables) => {
			const varsToUse = newVariables || variables;
			setLoading(true);
			setError(undefined);

			return fetchQuery<{ response: TData; variables: TVariables }>(environment, query, varsToUse, {
				networkCacheConfig: { metadata: context },
			})
				.toPromise()
				.then((result) => {
					setData(result);
					setLoading(false);
					return {
						data: result as TData,
						loading: false,
						networkStatus: 7, // NetworkStatus.ready
						stale: false,
					};
				})
				.catch((err) => {
					setError(err);
					setLoading(false);
					throw err;
				});
		},
		[environment, query, variables, context],
	);

	return {
		data,
		loading,
		error,
		refetch,
		variables,
		startPolling() {
			throw new Error(`[Relay Migration] 'startPolling' is not supported yet`);
		},
		stopPolling() {
			throw new Error(`[Relay Migration] 'stopPolling' is not supported yet`);
		},
		fetchMore() {
			throw new Error(`[Relay Migration] 'fetchMore' is not supported yet`);
		},
		get client(): any {
			throw new Error(
				`[Relay Migration] 'client' will not be supported; use other interop functions to achieve the desired result`,
			);
		},
		get networkStatus(): any {
			throw new Error(`[Relay Migration] 'networkStatus' is not supported yet`);
		},
	};
}
